<template>
  <div class="animated fadeIn">

    <!-- Page Header -->
    <b-row class="mb-3">
      <b-col>
        <b-button variant="outline-secondary" size="sm" @click="$router.push({ name: 'Device Configuration' })">
          <i class="fa fa-arrow-left"></i> Back to Settings
        </b-button>
        <span class="ml-3 page-heading">
          <strong>Calibrate Sensor</strong>
          <span v-if="sensor" class="text-muted"> — {{ sensor.label }}</span>
        </span>
      </b-col>
    </b-row>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border" role="status">
        <span class="sr-only">Loading...</span>
      </div>
      <p class="mt-2 text-muted">Loading sensor...</p>
    </div>

    <!-- Error -->
    <b-alert v-else-if="loadError" show variant="danger">
      <strong>Error loading sensor:</strong> {{ loadError.message }}
    </b-alert>

    <!-- Content -->
    <template v-else-if="sensor">

      <!-- Device Info Banner -->
      <b-card class="mb-3 device-banner">
        <b-row>
          <b-col md="3" sm="6">
            <div class="banner-field">
              <span class="banner-label">Device</span>
              <span class="banner-value">{{ sensor.config.device.name }}</span>
            </div>
          </b-col>
          <b-col md="2" sm="6">
            <div class="banner-field">
              <span class="banner-label">Serial</span>
              <span class="banner-value">{{ sensor.config.device.serial }}</span>
            </div>
          </b-col>
          <b-col md="2" sm="6">
            <div class="banner-field">
              <span class="banner-label">Type</span>
              <span class="banner-value text-capitalize">{{ sensor.config.device.type }}</span>
            </div>
          </b-col>
          <b-col md="2" sm="6">
            <div class="banner-field">
              <span class="banner-label">Site</span>
              <span class="banner-value">{{ deviceSite }}</span>
            </div>
          </b-col>
          <b-col md="1" sm="6">
            <div class="banner-field">
              <span class="banner-label">Elevation</span>
              <span class="banner-value">{{ sensor.config.device.elevation != null ? sensor.config.device.elevation + ' ft' : 'N/A' }}</span>
            </div>
          </b-col>
          <b-col md="2" sm="6">
            <div class="banner-field">
              <span class="banner-label">Sensor</span>
              <span class="banner-value">{{ sensor.label }}</span>
            </div>
          </b-col>
        </b-row>
        <b-row class="mt-2">
          <b-col md="2" sm="6">
            <div class="banner-field">
              <span class="banner-label">Current cal</span>
              <span class="banner-value" :class="currentCal < 0 ? 'text-danger' : currentCal > 0 ? 'text-success' : ''">
                {{ currentCal }}
              </span>
            </div>
          </b-col>
        </b-row>
      </b-card>

      <CalibrationDiagram
        :sensor-type="sensor.config.device.type"
        :cal="currentCal"
        :sample-raw="diagramSampleRaw"
      />

      <!-- Method tabs -->
      <b-card>
        <div slot="header">
          <i class="fa fa-crosshairs mr-1"></i> Calibration
        </div>

        <b-tabs content-class="pt-4">

          <!-- ── Tab 1: Live Wizard ── -->
          <b-tab active>
            <template #title>
              <i class="fa fa-magic mr-1"></i> Live Wizard (Particle)
            </template>

            <!-- Mode selector -->
            <b-form-group class="mb-3">
              <b-form-radio-group
                v-model="wizard.mode"
                :options="wizardModeOptions"
                button-variant="outline-primary"
                buttons
                size="sm"
                :disabled="wizard.running || prepCal.preparing"
                @change="resetWizardReads"
              ></b-form-radio-group>
            </b-form-group>

            <b-alert v-if="wizard.mode === 'inwater'" show variant="light" class="py-2 mb-3">
              <i class="fa fa-tint mr-1 text-primary"></i>
              <strong>In Water:</strong> Sends <code>doDepthRead</code> {{ wizard.readCount }} times.
              The <strong>median</strong> <code>val_f</code> is compared against your manual measurement to compute a new <code>cal</code> offset.
              Cal is zeroed before reads so the device reports raw values.
            </b-alert>

            <b-alert v-else-if="wizard.mode === 'outofwater'" show variant="light" class="py-2 mb-3">
              <i class="fa fa-arrow-up mr-1 text-success"></i>
              <strong>Out of Water:</strong> Sends <code>doDepthRead</code> {{ wizard.readCount }} times with the sensor in air.
              The <strong>median</strong> ADC value minus 5 is saved as <code>adcMin</code> on the device config, establishing a dry baseline.
            </b-alert>

            <b-alert v-if="wizard.deviceError" show variant="danger" class="mb-3">
              <i class="fa fa-exclamation-triangle mr-1"></i> {{ wizard.deviceError }}
            </b-alert>

            <div class="d-flex align-items-center mb-3">
              <b-button
                variant="primary"
                :disabled="wizard.running || prepCal.preparing"
                @click="runWizard"
              >
                <b-spinner v-if="wizard.running || wizard.loadingDevice || prepCal.preparing" small class="mr-1"></b-spinner>
                <i v-else class="fa fa-play mr-1"></i>
                <span v-if="prepCal.preparing">{{ prepCal.stage || 'Zeroing calibration' }}&hellip;</span>
                <span v-else-if="wizard.loadingDevice">Connecting&hellip;</span>
                <span v-else-if="wizard.running">Read {{ wizard.step }} of {{ wizard.readCount }}&hellip;</span>
                <span v-else>Start {{ wizard.readCount }} Reads</span>
              </b-button>
              <b-form-select
                v-model.number="wizard.readCount"
                :options="readCountOptions"
                size="sm"
                class="ml-2"
                style="max-width: 8rem;"
                :disabled="wizard.running || prepCal.preparing"
              ></b-form-select>
              <span v-if="wizard.running" class="ml-3 text-muted small">
                ~{{ (wizard.readCount - wizard.step) * 5 }}s remaining
              </span>
            </div>

            <b-table
              v-if="wizard.reads.length > 0"
              :items="wizard.reads"
              :fields="wizardFields"
              small
              striped
              bordered
              class="mb-3"
              style="max-width: 540px"
            >
              <template #cell(val_f)="{ item }">
                <b-spinner v-if="item.pending" small></b-spinner>
                <span v-else-if="item.error" class="text-danger small">{{ item.error }}</span>
                <span v-else-if="item.val_f !== null">{{ formatNum(item.val_f, 4) }} ft</span>
                <span v-else class="text-muted">—</span>
              </template>
              <template #cell(adc)="{ item }">
                <b-spinner v-if="item.pending" small></b-spinner>
                <span v-else-if="item.adc === null" class="text-muted">—</span>
                <span v-else :class="item.outOfBounds ? 'text-warning font-weight-bold' : ''">
                  {{ item.adc }}
                </span>
              </template>
              <template #cell(status)="{ item }">
                <b-spinner v-if="item.pending" small></b-spinner>
                <b-badge v-else-if="item.error" variant="danger">Error</b-badge>
                <b-badge v-else-if="item.outOfBounds" variant="warning">ADC out of range</b-badge>
                <b-badge v-else variant="success">OK</b-badge>
              </template>
            </b-table>

            <!-- Out of water result -->
            <template v-if="!wizard.running && wizard.mode === 'outofwater' && wizardAdcMedian !== null">
              <b-row>
                <b-col md="5">
                  <b-alert show variant="info" class="mb-3">
                    <strong>Median ADC ({{ wizardAdcReads.length }} valid read{{ wizardAdcReads.length !== 1 ? 's' : '' }}):</strong>
                    {{ formatNum(wizardAdcMedian, 1) }}
                    <div class="mt-1">
                      <strong>Proposed adcMin:</strong> {{ wizardProposedAdcMin }}
                      <span class="text-muted small ml-1">(median &minus; 5)</span>
                    </div>
                    <div v-if="sensor && sensor.config" class="mt-1 text-muted small">
                      Current adcMin: {{ sensor.config.adcMin != null ? sensor.config.adcMin : 'not set' }}
                    </div>
                  </b-alert>

                  <b-button variant="success" :disabled="saving" @click="applyOutOfWaterCalibration">
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    <span v-else><i class="fa fa-check mr-1"></i> Apply adcMin</span>
                  </b-button>
                </b-col>
              </b-row>
            </template>

            <!-- In water result -->
            <template v-if="!wizard.running && wizard.mode === 'inwater' && wizardMedian !== null">
              <b-row>
                <b-col md="5">
                  <b-alert show variant="info" class="mb-3">
                    <strong>Median raw depth ({{ wizardSuccessfulReads.length }} valid read{{ wizardSuccessfulReads.length !== 1 ? 's' : '' }}):</strong>
                    {{ formatNum(wizardMedian, 4) }} ft
                    <div v-if="wizardHasOutOfBounds" class="mt-1 text-warning small">
                      <i class="fa fa-exclamation-triangle"></i>
                      {{ wizardOutOfBoundsCount }} read{{ wizardOutOfBoundsCount !== 1 ? 's' : '' }} excluded (ADC out of range)
                    </div>
                  </b-alert>

                  <b-form-group label="Reference Value (ft) — your manual measurement" label-for="wiz-ref">
                    <b-input-group append="ft">
                      <b-form-input
                        id="wiz-ref"
                        v-model="wizard.wizRef"
                        type="number"
                        step="0.0001"
                        placeholder="Enter manual depth reading"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </b-col>

                <b-col md="6" offset-md="1" v-if="wizardNewCal !== null">
                  <b-card class="cal-preview mb-3" bg-variant="light">
                    <b-row class="text-center">
                      <b-col cols="3">
                        <div class="preview-label">Current cal</div>
                        <div class="preview-value">{{ formatNum(currentCal, 4) }}</div>
                      </b-col>
                      <b-col cols="3">
                        <div class="preview-label">Median Raw Depth</div>
                        <div class="preview-value">{{ formatNum(wizardMedian, 4) }}</div>
                      </b-col>
                      <b-col cols="3">
                        <div class="preview-label">Manual</div>
                        <div class="preview-value">{{ formatNum(wizard.wizRef, 4) }}</div>
                      </b-col>
                      <b-col cols="3">
                        <div class="preview-label">New cal</div>
                        <div class="preview-value" :class="wizardNewCal >= 0 ? 'text-success' : 'text-danger'">
                          {{ formatNum(wizardNewCal, 4) }}
                        </div>
                      </b-col>
                    </b-row>
                    <div class="small text-muted text-center mt-2">
                      <template v-if="isDepthToWaterSensor">
                        New cal = &minus;({{ formatNum(wizard.wizRef, 4) }} + {{ formatNum(Math.abs(wizardMedian), 4) }}) = {{ formatNum(wizardNewCal, 4) }}
                        <div class="small">(well: firmware publishes DTW as <code>&minus;cal &minus; raw</code>)</div>
                      </template>
                      <template v-else>
                        New cal = {{ formatNum(currentCal, 4) }} + ({{ formatNum(wizard.wizRef, 4) }} &minus; {{ formatNum(wizardMedian, 4) }}) = {{ formatNum(wizardNewCal, 4) }}
                      </template>
                    </div>
                  </b-card>

                  <b-button variant="success" :disabled="saving" @click="applyWizardCalibration">
                    <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                    <span v-else><i class="fa fa-check mr-1"></i> Apply Calibration</span>
                  </b-button>
                </b-col>
              </b-row>
            </template>
          </b-tab>

          <!-- ── Tab 2: Manual ── -->
          <b-tab>
            <template #title>
              <i class="fa fa-calendar mr-1"></i> Manual (Historical Data)
            </template>

            <p class="text-muted small mb-3">
              Fetch historical sensor readings for a given day, select the data point
              that corresponds to your reference measurement, then enter your manual reading.
              The system computes the calibration offset automatically.
            </p>

            <b-row>
              <b-col md="5">
                <b-form-group label="Metric to Calibrate" label-for="cal-metric">
                  <b-form-select
                    id="cal-metric"
                    v-model="cal.metric"
                    :options="calibrationMetricOptions"
                    @change="resetCalibration"
                  ></b-form-select>
                </b-form-group>

                <b-form-group label="Date" label-for="cal-date">
                  <date-picker
                    id="cal-date"
                    v-model="cal.date"
                    type="date"
                    format="YYYY-MM-DD"
                    :not-after="maxDate"
                    input-class="form-control"
                    placeholder="Select date..."
                    @change="resetCalibration"
                  ></date-picker>
                </b-form-group>

                <b-button
                  variant="outline-primary"
                  size="sm"
                  :disabled="!cal.metric || !cal.date || cal.fetching"
                  @click="fetchCalibrationReading"
                >
                  <span v-if="cal.fetching">
                    <b-spinner small></b-spinner> Fetching...
                  </span>
                  <span v-else>
                    <i class="fa fa-search"></i> Fetch Readings
                  </span>
                </b-button>

                <b-alert v-if="cal.noData" show variant="warning" class="mt-3 mb-0">
                  <i class="fa fa-exclamation-triangle"></i>
                  No data found for the selected date.
                </b-alert>

                <template v-if="cal.availableReadings.length > 0">
                  <b-form-group class="mt-3" label="Select Data Point" label-for="cal-point">
                    <b-form-select
                      id="cal-point"
                      v-model="cal.selectedIndex"
                      :options="availableReadingOptions"
                      @change="cal.reference = null"
                    ></b-form-select>
                    <small class="form-text text-muted">
                      {{ cal.availableReadings.length }} reading{{ cal.availableReadings.length !== 1 ? 's' : '' }} found
                    </small>
                  </b-form-group>
                </template>

                <template v-if="selectedReading !== null">
                  <b-alert show variant="info" class="mt-2 mb-3">
                    <strong>Recorded reading:</strong>
                    {{ formatNum(selectedReading.displayValue, 4) }} {{ selectedCalMetric ? selectedCalMetric.unit : '' }}
                    <div v-if="selectedCalMetric && selectedCalMetric.derived && sensor.config.device.elevation != null">
                      <strong>Depth to water:</strong>
                      {{ formatNum(sensor.config.device.elevation - selectedReading.displayValue, 4) }} ft
                    </div>
                  </b-alert>

                  <b-form-group :label="referenceLabel" label-for="cal-ref">
                    <b-input-group :append="selectedCalMetric ? selectedCalMetric.unit : ''">
                      <b-form-input
                        id="cal-ref"
                        v-model.number="cal.reference"
                        type="number"
                        step="0.0001"
                        placeholder="Enter your measured value"
                      ></b-form-input>
                    </b-input-group>
                  </b-form-group>
                </template>
              </b-col>

              <b-col md="6" offset-md="1" v-if="newCalValue !== null">
                <b-card class="cal-preview mb-3" bg-variant="light">
                  <b-row class="text-center">
                    <b-col cols="3">
                      <div class="preview-label">Current cal</div>
                      <div class="preview-value">{{ formatNum(currentCal, 4) }}</div>
                    </b-col>
                    <b-col cols="3">
                      <div class="preview-label">Recorded</div>
                      <div class="preview-value">{{ formatNum(selectedReading.displayValue, 4) }}</div>
                    </b-col>
                    <b-col cols="3">
                      <div class="preview-label">Manual</div>
                      <div class="preview-value">{{ formatNum(cal.reference, 4) }}</div>
                    </b-col>
                    <b-col cols="3">
                      <div class="preview-label">New cal</div>
                      <div class="preview-value" :class="newCalValue >= 0 ? 'text-success' : 'text-danger'">
                        {{ formatNum(newCalValue, 4) }}
                      </div>
                    </b-col>
                  </b-row>
                  <div class="small text-muted text-center mt-2">
                    <template v-if="isDepthToWaterSensor">
                      New cal = {{ formatNum(currentCal, 4) }} &minus; ({{ formatNum(cal.reference, 4) }} &minus; {{ formatNum(selectedReading.displayValue, 4) }}) = {{ formatNum(newCalValue, 4) }}
                      <div class="small">(well: cal adjustment sign is inverted vs. direct-depth sensors)</div>
                    </template>
                    <template v-else>
                      New cal = {{ formatNum(currentCal, 4) }} + ({{ formatNum(cal.reference, 4) }} &minus; {{ formatNum(selectedReading.displayValue, 4) }}) = {{ formatNum(newCalValue, 4) }}
                    </template>
                  </div>
                </b-card>

                <b-button variant="success" :disabled="saving" @click="applyCalibration">
                  <b-spinner v-if="saving" small class="mr-1"></b-spinner>
                  <span v-else><i class="fa fa-check mr-1"></i> Apply Calibration</span>
                </b-button>
              </b-col>
            </b-row>
          </b-tab>

        </b-tabs>
      </b-card>

    </template>

  </div>
</template>

<script>
import DatePicker from 'vue2-datepicker'
import moment from 'moment-timezone'
import { DEVICE_SENSOR_QUERY, UPDATE_DEVICE_SENSOR_MUTATION, UPDATE_DEVICE_CONFIG_MUTATION, METRICS_QUERY } from '@/constants/graphql'
import { getAccessToken } from '@/lib/auth'
import CalibrationDiagram from '@/components/CalibrationDiagram.vue'

const API_BASE = (process.env.VUE_APP_API_ENDPOINT || 'https://apiv2.leaf.ai/graphql').replace('/graphql', '')

const CALIBRATION_METRICS = {
  well: [
    { value: 'depth_f', text: 'Depth to Water (ft)', unit: 'ft', apiMetric: 'depth_f', derived: false }
  ],
  flume: [
    { value: 'flow_cfs', text: 'Flow (cfs)', unit: 'cfs', apiMetric: 'flow_cfs', derived: false },
    { value: 'level_ft', text: 'Level (ft)', unit: 'ft', apiMetric: 'level_ft', derived: false }
  ],
  rain: [
    { value: 'rain_in', text: 'Rainfall (in)', unit: 'in', apiMetric: 'rain_in', derived: false },
    { value: 'intensity', text: 'Precip Rate (in/hr)', unit: 'in/hr', apiMetric: 'intensity', derived: false }
  ],
  bubbler: [
    { value: 'pres_hpa', text: 'Pressure (hPa)', unit: 'hPa', apiMetric: 'pres_hpa', derived: false },
    { value: 'depth_f', text: 'Depth (ft)', unit: 'ft', apiMetric: 'depth_f', derived: false }
  ],
  plunge: [
    { value: 'tmp_f', text: 'Temperature (°F)', unit: '°F', apiMetric: 'tmp_f', derived: false },
    { value: 'depth_f', text: 'Depth (ft)', unit: 'ft', apiMetric: 'depth_f', derived: false }
  ],
  river: [
    { value: 'depth_f', text: 'River Stage (ft)', unit: 'ft', apiMetric: 'depth_f', derived: false },
    { value: 'pres_hpa', text: 'Pressure (hPa)', unit: 'hPa', apiMetric: 'pres_hpa', derived: false }
  ]
}

const DEFAULT_METRICS = [
  { value: 'tmp_f', text: 'Temperature (°F)', unit: '°F', apiMetric: 'tmp_f', derived: false }
]

export default {
  name: 'SensorCalibration',
  components: { DatePicker, CalibrationDiagram },
  props: {
    sensorId: { type: String, required: true }
  },
  data () {
    return {
      loading: false,
      loadError: null,
      sensor: null,
      saving: false,
      currentCal: 0,
      maxDate: new Date(),
      cal: {
        metric: null,
        date: null,
        fetching: false,
        availableReadings: [],
        selectedIndex: null,
        noData: false,
        reference: null
      },
      prepCal: {
        preparing: false,
        error: null,
        originalCal: null,
        stage: ''
      },
      wizard: {
        mode: 'inwater',
        running: false,
        step: 0,
        reads: [],
        deviceError: null,
        loadingDevice: false,
        particleDeviceId: null,
        wizRef: null,
        readCount: 5
      },
      readCountOptions: [
        { value: 3, text: '3 reads' },
        { value: 5, text: '5 reads' },
        { value: 7, text: '7 reads' },
        { value: 9, text: '9 reads' },
        { value: 11, text: '11 reads' }
      ],
      wizardModeOptions: [
        { value: 'inwater', text: '💧 In Water' },
        { value: 'outofwater', text: '⬆️ Out of Water' }
      ],
      wizardFields: [
        { key: 'index', label: '#', thStyle: { width: '3rem' } },
        { key: 'val_f', label: 'Raw Depth (ft)' },
        { key: 'adc', label: 'ADC Reading' },
        { key: 'status', label: 'Status' }
      ]
    }
  },
  computed: {
    deviceSite () {
      return (this.sensor && this.sensor.config.device.site && this.sensor.config.device.site.name) || 'N/A'
    },
    // Whether this device's firmware inverts the raw sensor value when applying cal.
    // Wells: submersible transducer reports water column above sensor; firmware computes
    // depth-to-water as `-cal - raw`, so calibration adjustments have inverted sign
    // relative to direct-depth sensors (flumes, plunge, bubbler).
    //
    // Rivers reuse the well sensor and the well calibration procedure — cal is set to
    // -(benchmark-to-water + sensor reading), i.e. the sensor's depth below a surveyed
    // benchmark — so they follow the well branch, not the additive-trim branch.
    isDepthToWaterSensor () {
      var type = (this.sensor && this.sensor.config && this.sensor.config.device)
        ? this.sensor.config.device.type
        : null
      return type === 'well' || type === 'river'
    },
    // Live "raw" value to feed the diagram's worked example, if we have one.
    // Prefer the wizard's just-measured average (taken with cal=0, so it IS raw).
    // Fall back to the manual-mode reading if the user has selected one — for wells
    // that value has cal applied, so we back it out: raw = |cal| − device.
    diagramSampleRaw () {
      if (this.wizardMedian !== null && !isNaN(this.wizardMedian)) {
        return Math.abs(this.wizardMedian)
      }
      if (this.selectedReading && !isNaN(this.selectedReading.displayValue)) {
        if (this.isDepthToWaterSensor) {
          return Math.abs(this.currentCal) - this.selectedReading.displayValue
        }
        return this.selectedReading.displayValue - this.currentCal
      }
      return null
    },
    calibrationMetricOptions () {
      if (!this.sensor) return []
      var deviceType = this.sensor.config.device.type
      var metrics = CALIBRATION_METRICS[deviceType] || DEFAULT_METRICS
      return [{ value: null, text: 'Select metric...' }].concat(metrics.map(function (m) { return { value: m.value, text: m.text } }))
    },
    selectedCalMetric () {
      if (!this.cal.metric || !this.sensor) return null
      var deviceType = this.sensor.config.device.type
      var metrics = CALIBRATION_METRICS[deviceType] || DEFAULT_METRICS
      return metrics.find(function (m) { return m.value === this.cal.metric }.bind(this)) || null
    },
    referenceLabel () {
      if (!this.selectedCalMetric) return 'Reference Value'
      return 'Reference Value (' + this.selectedCalMetric.unit + ') \u2014 your manual measurement'
    },
    availableReadingOptions () {
      var self = this
      var opts = this.cal.availableReadings.map(function (r, i) {
        return {
          value: i,
          text: self.formatTime(r.time) + '  \u2014  ' + self.formatNum(r.displayValue, 4) + ' ' + (self.selectedCalMetric ? self.selectedCalMetric.unit : '')
        }
      })
      return [{ value: null, text: 'Select a data point...' }].concat(opts)
    },
    selectedReading () {
      if (this.cal.selectedIndex === null || !this.cal.availableReadings.length) return null
      return this.cal.availableReadings[this.cal.selectedIndex] || null
    },
    adjustment () {
      if (!this.selectedReading || this.cal.reference === null || this.cal.reference === '') return null
      var ref = parseFloat(this.cal.reference)
      if (isNaN(ref)) return null
      // Wells: cal encodes negative sensor depth; adjustment sign is inverted.
      var delta = ref - this.selectedReading.displayValue
      return parseFloat((this.isDepthToWaterSensor ? -delta : delta).toFixed(6))
    },
    newCalValue () {
      if (this.adjustment === null) return null
      return parseFloat((this.currentCal + this.adjustment).toFixed(6))
    },
    wizardSuccessfulReads () {
      return this.wizard.reads.filter(function (r) { return r.val_f !== null && !r.outOfBounds && !r.error })
    },
    // Median depth from the successful reads. Robust to a single spurious reading
    // (bubble, transient, comm glitch) — a mean would let one outlier skew the cal.
    wizardMedian () {
      var reads = this.wizardSuccessfulReads
      if (!reads.length) return null
      var values = reads.map(function (r) { return r.val_f }).slice().sort(function (a, b) { return a - b })
      var n = values.length
      var mid = Math.floor(n / 2)
      var m = (n % 2 === 1) ? values[mid] : (values[mid - 1] + values[mid]) / 2
      return parseFloat(m.toFixed(6))
    },
    wizardHasOutOfBounds () {
      return this.wizard.reads.some(function (r) { return r.outOfBounds })
    },
    wizardOutOfBoundsCount () {
      return this.wizard.reads.filter(function (r) { return r.outOfBounds }).length
    },
    wizardNewCal () {
      if (this.wizardMedian === null) return null
      if (this.wizard.wizRef === null || this.wizard.wizRef === '') return null
      var ref = parseFloat(this.wizard.wizRef)
      if (isNaN(ref)) return null
      // The wizard has already zeroed cal on the device before reading, so wizardMedian
      // is the raw sensor value (water column above sensor for wells; water depth for flumes).
      // Wells: new_cal = -(ref + |raw|) — makes firmware publish DTW = ref via `-cal - raw`.
      // Everything else: new_cal = ref - raw — standard additive offset.
      if (this.isDepthToWaterSensor) {
        return parseFloat((-(ref + Math.abs(this.wizardMedian))).toFixed(6))
      }
      return parseFloat((this.currentCal + (ref - this.wizardMedian)).toFixed(6))
    },
    wizardAdcReads () {
      return this.wizard.reads.filter(function (r) { return r.adc !== null && !r.error })
    },
    wizardAdcMedian () {
      var reads = this.wizardAdcReads
      if (!reads.length) return null
      var values = reads.map(function (r) { return r.adc }).slice().sort(function (a, b) { return a - b })
      var n = values.length
      var mid = Math.floor(n / 2)
      return (n % 2 === 1) ? values[mid] : (values[mid - 1] + values[mid]) / 2
    },
    wizardProposedAdcMin () {
      if (this.wizardAdcMedian === null) return null
      return Math.round(this.wizardAdcMedian - 5)
    }
  },
  async created () {
    await this.loadSensor()
  },
  methods: {
    async loadSensor () {
      this.loading = true
      this.loadError = null
      try {
        var result = await this.$apollo.query({
          query: DEVICE_SENSOR_QUERY,
          variables: { id: this.sensorId },
          fetchPolicy: 'network-only'
        })
        this.sensor = result.data.deviceSensor
        if (this.sensor) {
          this.currentCal = this.sensor.cal != null ? this.sensor.cal : 0
        }
      } catch (err) {
        this.loadError = err
      } finally {
        this.loading = false
      }
    },

    resetCalibration () {
      this.cal.availableReadings = []
      this.cal.selectedIndex = null
      this.cal.noData = false
      this.cal.reference = null
    },

    async fetchCalibrationReading () {
      if (!this.cal.metric || !this.cal.date || !this.selectedCalMetric) return
      this.cal.fetching = true
      this.resetCalibration()

      var apiMetric = this.selectedCalMetric.apiMetric
      var dayStart = new Date(this.cal.date)
      dayStart.setHours(0, 0, 0, 0)
      var dayEnd = new Date(this.cal.date)
      dayEnd.setHours(23, 59, 59, 999)

      try {
        var result = await this.$apollo.query({
          query: METRICS_QUERY,
          variables: {
            where: {
              device: this.sensor.config.device.serial,
              type: this.sensor.config.device.type,
              metric: apiMetric,
              start: dayStart,
              end: dayEnd,
              limit: 5000
            }
          },
          fetchPolicy: 'network-only'
        })

        var data = result.data.metrics && result.data.metrics.data
        if (!data || Object.keys(data).length === 0) { this.cal.noData = true; return }

        var deviceKey = Object.keys(data)[0]
        var deviceData = data[deviceKey]
        if (!deviceData || !deviceData.values || !deviceData.values.length) { this.cal.noData = true; return }

        var columns = deviceData.columns
        var timeIndex = columns.indexOf('time') !== -1 ? columns.indexOf('time') : 2
        var metricIndex = columns.indexOf(apiMetric)
        if (metricIndex === -1) { this.cal.noData = true; return }

        var elevation = (this.sensor.config.device.elevation) || 0
        var isDerived = this.selectedCalMetric.derived

        this.cal.availableReadings = deviceData.values
          .filter(function (row) { return row[metricIndex] !== null && row[metricIndex] !== undefined })
          .sort(function (a, b) { return a[timeIndex] - b[timeIndex] })
          .map(function (row) {
            var rawVal = parseFloat(row[metricIndex])
            return {
              time: new Date(row[timeIndex] * 1000),
              rawValue: rawVal,
              displayValue: isDerived ? elevation - rawVal : rawVal
            }
          })

        if (this.cal.availableReadings.length === 0) this.cal.noData = true
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching calibration readings:', err)
        this.cal.noData = true
      } finally {
        this.cal.fetching = false
      }
    },

    async applyCalibration () {
      if (this.newCalValue === null || this.saving) return
      this.saving = true
      var newCal = this.newCalValue
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_SENSOR_MUTATION,
          variables: { id: this.sensor.id, input: { cal: newCal } }
        })
        this.currentCal = newCal
        this.cal.date = null
        this.resetCalibration()
        this.$bvToast.toast('Calibration applied. New cal: ' + newCal, {
          title: 'Calibration Saved', variant: 'success', solid: true, autoHideDelay: 4000
        })
        this.sendRefreshConfig()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error applying calibration:', err)
        this.$bvToast.toast('Failed to apply calibration. Please try again.', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.saving = false
      }
    },

    // ── Wizard ─────────────────────────────────────────────────────────────────

    sleep (ms) {
      return new Promise(function (resolve) { setTimeout(resolve, ms) })
    },

    async particleApi (method, path, body) {
      var opts = {
        method: method,
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + getAccessToken() }
      }
      if (body) opts.body = JSON.stringify(body)
      var res = await fetch(API_BASE + path, opts)
      if (!res.ok) {
        var errMsg = 'HTTP ' + res.status
        try {
          var json = await res.json()
          if (json && json.error) errMsg = json.error
        } catch (e) { /* ignore */ }
        throw new Error(errMsg)
      }
      return res.json()
    },

    async loadWizardParticleDevice () {
      this.wizard.loadingDevice = true
      this.wizard.deviceError = null
      try {
        var serial = (this.sensor.config.device.serial || '').toLowerCase()
        var matchFn = function (pd) {
          return (pd.serialNumber && pd.serialNumber.toLowerCase() === serial) ||
                 (pd.particleId && pd.particleId.toLowerCase() === serial) ||
                 (pd.name && pd.name.toLowerCase() === serial)
        }
        var devices = await this.particleApi('GET', '/api/particle/devices')
        var found = (devices || []).find(matchFn) || null
        if (!found) {
          await this.particleApi('POST', '/api/particle/sync')
          var synced = await this.particleApi('GET', '/api/particle/devices')
          found = (synced || []).find(matchFn) || null
        }
        if (found) {
          this.wizard.particleDeviceId = found.id
        } else {
          this.wizard.deviceError = 'No Particle device found matching serial ' + this.sensor.config.device.serial
        }
      } catch (e) {
        this.wizard.deviceError = 'Failed to connect to Particle Cloud: ' + e.message
      } finally {
        this.wizard.loadingDevice = false
      }
    },

    findDepthReadEvent (events, correlationId, afterTime) {
      var arr = Array.isArray(events) ? events : (events.events || [])
      for (var i = 0; i < arr.length; i++) {
        var ev = arr[i]
        var evTime = new Date(ev.publishedAt || ev.created)
        if (evTime < afterTime) break
        try {
          var d = JSON.parse(ev.data || '{}')
          if (correlationId && d.cid && d.cid === correlationId) return ev
          if (d.val_f !== undefined) return ev
        } catch (e) { /* not JSON */ }
      }
      return null
    },

    parseDepthEvent (data) {
      try {
        var d = JSON.parse(data || '{}')
        return {
          val_f: d.val_f !== undefined ? parseFloat(d.val_f) : null,
          adc: d.adc !== undefined ? parseInt(d.adc, 10) : null
        }
      } catch (e) {
        return { val_f: null, adc: null }
      }
    },

    isAdcOutOfBounds (adc) {
      if (adc === null || adc === undefined) return false
      var config = this.sensor && this.sensor.config
      if (!config) return false
      if (config.adcMin != null && adc < config.adcMin) return true
      if (config.adcMax != null && adc > config.adcMax) return true
      return false
    },

    // Sends a Particle function call with correlation and blocks until the device
    // echoes the correlation ID back in an event (i.e. actually processed the command).
    // Throws if no ack event appears within timeoutMs.
    async sendCommandAwaitAck (deviceId, commandName, argument, timeoutMs) {
      var timeout = timeoutMs || 10000
      var pollInterval = 1000

      var cmdResult = await this.particleApi(
        'POST',
        '/api/particle/devices/' + deviceId + '/commands',
        { commandName: commandName, argument: argument || '', useCorrelation: true }
      )
      var cmd = cmdResult.command || {}
      var cmdTime = new Date(cmd.sentAt || cmd.requestedAt || Date.now())
      var correlationId = cmd.correlationId || null

      // If the backend didn't produce a correlation ID we can't wait for confirmation;
      // fall back to the fire-and-forget behavior.
      if (!correlationId) return cmdResult

      var deadline = Date.now() + timeout
      while (Date.now() < deadline) {
        await this.sleep(pollInterval)
        try {
          var events = await this.particleApi('GET', '/api/particle/devices/' + deviceId + '/events?limit=30')
          var arr = Array.isArray(events) ? events : (events.events || [])
          for (var i = 0; i < arr.length; i++) {
            var ev = arr[i]
            var evTime = new Date(ev.publishedAt || ev.created)
            if (evTime < cmdTime) break
            try {
              var d = JSON.parse(ev.data || '{}')
              if (d.cid && d.cid === correlationId) return cmdResult
            } catch (e) { /* not JSON, keep scanning */ }
          }
        } catch (pollErr) { /* transient poll error; keep trying until deadline */ }
      }
      throw new Error('Device did not acknowledge ' + commandName + ' within ' + Math.round(timeout / 1000) + 's.')
    },

    async prepareCalibration () {
      this.prepCal.preparing = true
      this.prepCal.error = null
      this.prepCal.originalCal = this.currentCal
      this.prepCal.stage = 'Zeroing calibration'

      try {
        // Ensure we have the Particle device ID
        if (!this.wizard.particleDeviceId) {
          await this.loadWizardParticleDevice()
        }

        // Zero the cal value so the device reads raw
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_SENSOR_MUTATION,
          variables: { id: this.sensor.id, input: { cal: 0 } }
        })
        this.currentCal = 0

        // Send refreshConfig and check the POST response to confirm the device
        // returned from the function call. The device doesn't publish an ack event for
        // this command, so we rely on Particle's synchronous function-call return
        // (command.status === 'succeeded' + particleReturnValue >= 0), then sleep
        // briefly to let the device finish the async config fetch.
        if (this.wizard.particleDeviceId) {
          this.prepCal.stage = 'Sending refreshConfig'
          try {
            var cmdResult = await this.particleApi(
              'POST',
              '/api/particle/devices/' + this.wizard.particleDeviceId + '/commands',
              { commandName: 'refreshConfig', argument: '', useCorrelation: false }
            )
            var cmd = (cmdResult && cmdResult.command) || {}
            var status = cmd.status
            var returnValue = (cmdResult && typeof cmdResult.particleReturnValue === 'number')
              ? cmdResult.particleReturnValue
              : cmd.particleReturnValue
            if (status === 'failed' || (typeof returnValue === 'number' && returnValue < 0)) {
              throw new Error('Device reported refreshConfig failure' + (returnValue != null ? ' (returned ' + returnValue + ')' : '') + '.')
            }
            if (status && status !== 'succeeded' && status !== 'sent' && status !== 'acknowledged') {
              throw new Error('Unexpected refreshConfig status: ' + status)
            }

            // Device accepted the call. Give it a moment to complete the async config fetch.
            this.prepCal.stage = 'Waiting for device to apply cal=0'
            await this.sleep(5000)
          } catch (particleErr) {
            // refreshConfig failed — revert cal before bailing
            await this.$apollo.mutate({
              mutation: UPDATE_DEVICE_SENSOR_MUTATION,
              variables: { id: this.sensor.id, input: { cal: this.prepCal.originalCal } }
            })
            this.currentCal = this.prepCal.originalCal
            throw new Error('refreshConfig failed (' + particleErr.message + '). Calibration value reverted to ' + this.prepCal.originalCal + '.')
          }
        }
      } finally {
        this.prepCal.preparing = false
        this.prepCal.stage = ''
      }
    },

    resetWizardReads () {
      this.wizard.reads = []
      this.wizard.step = 0
      this.wizard.deviceError = null
      this.wizard.wizRef = null
    },

    async runWizard () {
      this.resetWizardReads()
      this.prepCal.error = null

      if (this.wizard.mode === 'inwater') {
        // Zero cal and push refreshConfig to device before taking readings
        try {
          await this.prepareCalibration()
        } catch (err) {
          this.wizard.deviceError = err.message
          return
        }
      } else {
        // Out of water — just ensure we have the Particle device ID, no cal changes
        if (!this.wizard.particleDeviceId) {
          this.wizard.loadingDevice = true
          try {
            await this.loadWizardParticleDevice()
          } catch (err) {
            this.wizard.deviceError = err.message
            this.wizard.loadingDevice = false
            return
          }
          this.wizard.loadingDevice = false
        }
      }

      if (!this.wizard.particleDeviceId) {
        this.wizard.deviceError = 'No Particle device found matching serial ' + (this.sensor ? this.sensor.config.device.serial : '')
        return
      }

      this.wizard.running = true
      var readCount = this.wizard.readCount || 5

      for (var i = 0; i < readCount; i++) {
        this.wizard.step = i + 1
        this.wizard.reads.push({ index: i + 1, val_f: null, adc: null, outOfBounds: false, error: null, pending: true })
        var idx = this.wizard.reads.length - 1

        try {
          var cmdResult = await this.particleApi(
            'POST',
            '/api/particle/devices/' + this.wizard.particleDeviceId + '/commands',
            { commandName: 'doDepthRead', argument: '', useCorrelation: true }
          )
          var cmd = cmdResult.command || {}
          var cmdTime = new Date(cmd.sentAt || cmd.requestedAt || Date.now())
          var correlationId = cmd.correlationId || null

          await this.sleep(4000)

          var events = await this.particleApi('GET', '/api/particle/devices/' + this.wizard.particleDeviceId + '/events?limit=30')
          var ev = this.findDepthReadEvent(events, correlationId, cmdTime)

          if (ev) {
            var parsed = this.parseDepthEvent(ev.data)
            var oob = this.isAdcOutOfBounds(parsed.adc)
            this.$set(this.wizard.reads, idx, { index: i + 1, val_f: parsed.val_f, adc: parsed.adc, outOfBounds: oob, error: null, pending: false })
          } else {
            this.$set(this.wizard.reads, idx, { index: i + 1, val_f: null, adc: null, outOfBounds: false, error: 'No response received', pending: false })
          }
        } catch (e) {
          this.$set(this.wizard.reads, idx, { index: i + 1, val_f: null, adc: null, outOfBounds: false, error: e.message, pending: false })
        }

        if (i < readCount - 1) await this.sleep(1000)
      }

      this.wizard.running = false
    },

    async applyWizardCalibration () {
      if (this.wizardNewCal === null || this.saving) return
      this.saving = true
      var newCal = this.wizardNewCal
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_SENSOR_MUTATION,
          variables: { id: this.sensor.id, input: { cal: newCal } }
        })
        this.currentCal = newCal
        this.wizard.wizRef = null
        this.$bvToast.toast('Calibration applied. New cal: ' + newCal, {
          title: 'Calibration Saved', variant: 'success', solid: true, autoHideDelay: 4000
        })
        this.sendRefreshConfig()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error applying wizard calibration:', err)
        this.$bvToast.toast('Failed to apply calibration. Please try again.', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.saving = false
      }
    },

    async applyOutOfWaterCalibration () {
      if (this.wizardProposedAdcMin === null || this.saving) return
      this.saving = true
      var newAdcMin = this.wizardProposedAdcMin
      try {
        await this.$apollo.mutate({
          mutation: UPDATE_DEVICE_CONFIG_MUTATION,
          variables: { id: this.sensor.config.id, input: { adcMin: newAdcMin } }
        })
        this.$bvToast.toast('adcMin set to ' + newAdcMin + '.', {
          title: 'adcMin Saved', variant: 'success', solid: true, autoHideDelay: 4000
        })
        this.sendRefreshConfig()
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error applying out-of-water calibration:', err)
        this.$bvToast.toast('Failed to save adcMin. Please try again.', {
          title: 'Error', variant: 'danger', solid: true
        })
      } finally {
        this.saving = false
      }
    },

    async sendRefreshConfig () {
      this.$bvToast.toast('Sending refresh config to device\u2026', {
        title: 'Refreshing Device', variant: 'info', solid: true, autoHideDelay: 3000
      })
      try {
        // Use cached particle device ID from wizard if available, otherwise look it up
        if (!this.wizard.particleDeviceId) {
          await this.loadWizardParticleDevice()
        }
        if (!this.wizard.particleDeviceId) {
          this.$bvToast.toast('Could not find Particle device to send refresh command.', {
            title: 'Refresh Skipped', variant: 'warning', solid: true, autoHideDelay: 5000
          })
          return
        }
        await this.particleApi('POST', '/api/particle/devices/' + this.wizard.particleDeviceId + '/commands', {
          commandName: 'refreshConfig',
          argument: '',
          useCorrelation: false
        })
        this.$bvToast.toast('Device config refresh sent successfully.', {
          title: 'Device Refreshed', variant: 'success', solid: true, autoHideDelay: 4000
        })
      } catch (e) {
        this.$bvToast.toast('Config refresh failed: ' + e.message + '. The calibration was still saved.', {
          title: 'Refresh Failed', variant: 'warning', solid: true, autoHideDelay: 6000
        })
      }
    },

    formatNum (val, decimals) {
      if (val === null || val === undefined) return '--'
      return parseFloat(val).toFixed(decimals != null ? decimals : 2)
    },

    formatTime (val) {
      if (!val) return '--'
      return moment(val).format('YYYY-MM-DD HH:mm:ss')
    }
  }
}
</script>

<style scoped>
.page-heading {
  font-size: 1.1rem;
  vertical-align: middle;
}

.device-banner {
  background-color: #f8f9fa;
}

.banner-field {
  display: flex;
  flex-direction: column;
  padding: 0.25rem 0;
}

.banner-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  letter-spacing: 0.05em;
}

.banner-value {
  font-weight: 600;
  font-size: 0.95rem;
}

.cal-preview {
  border: 1px solid #dee2e6;
}

.preview-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  color: #6c757d;
  margin-bottom: 0.25rem;
}

.preview-value {
  font-size: 1.1rem;
  font-weight: 600;
}
</style>
